import axios from 'config'
import UrlBuilder from 'services/util/url-builder'
import {
	InvoiceDetailsByInvoiceIdRequest,
	PagedInvoiceDetailsByInvoiceIdRequest,
	RegenerateInvoiceByIdRequest,
} from 'types/invoices'
import { API_NAME } from '../constants'
import { BasePagedResult, InvoiceDetailsByInvoiceIdResponse } from '../types/invoice-details-by-invoice-id-response'
import { InvoiceReconciliationDetailsResponse } from '../types/invoice-reconciliation-details-response'
import { RemittanceDetail } from '../types/invoice-remittance-details'
import { InvoicesGetRequest } from '../types/invoices-get-request'
import { InvoicesResponse } from '../types/invoices-response'

const urlBuilder: UrlBuilder = new UrlBuilder(API_NAME)
export const INVOICES = 'invoices'

export class InvoicesService {
	static getInvoices({
		endDate,
		order,
		orderBy,
		organizationId,
		rowsPerPage,
		status,
		page,
		startDate,
	}: InvoicesGetRequest): Promise<InvoicesResponse> {
		const url: string = urlBuilder
			.setEndpoint(INVOICES)
			.setQueryParams(
				new URLSearchParams({
					...(endDate ? { endDate } : {}),
					...(orderBy ? { sortBy: `${orderBy}:${order}` } : {}),
					...(organizationId ? { organizationId } : {}),
					...(status ? { status } : {}),
					...(page ? { page } : {}),
					...(rowsPerPage ? { pageSize: rowsPerPage } : {}),
					...(startDate ? { startDate } : {}),
				}),
			)
			.url()

		return axios.get(url)
	}

	static getInvoiceById({ invoiceId }: { invoiceId: string }): Promise<InvoiceReconciliationDetailsResponse> {
		const url: string = urlBuilder.setEndpoint(`${INVOICES}/${invoiceId}`).url()

		return axios.get(url)
	}

	static getInvoiceRemittanceDetails(invoiceId: string): Promise<RemittanceDetail[]> {
		const url = urlBuilder.setEndpoint(`${INVOICES}/${invoiceId}/remittanceDetails`).url()

		return axios.get(url)
	}

	static getPagedInvoiceDetailsByInvoiceId({
		invoiceId,
		queryParams: { endDate, employeeId, discrepancyCategory, startDate, invoiceTransactionType, page, pageSize },
	}: PagedInvoiceDetailsByInvoiceIdRequest): Promise<BasePagedResult<InvoiceDetailsByInvoiceIdResponse>> {
		const url: string = urlBuilder
			.setEndpoint(`${INVOICES}/${invoiceId}/paged-invoice-details`)
			.setQueryParams(
				new URLSearchParams({
					...(endDate ? { endDate } : {}),
					...(startDate ? { startDate } : {}),
					...(employeeId ? { employeeId } : {}),
					...(discrepancyCategory ? { discrepancyCategory } : {}),
					...(invoiceTransactionType ? { invoiceTransactionType } : {}),
					...(page ? { page: page.toString() } : {}),
					...(pageSize ? { pageSize: pageSize.toString() } : {}),
				}),
			)
			.url()

		return axios.get(url)
	}

	static downloadInvoiceDetailsReport({
		invoiceId,
		queryParams: { endDate, employeeId, discrepancyCategory, startDate, invoiceTransactionType },
	}: InvoiceDetailsByInvoiceIdRequest): Promise<Blob> {
		const url: string = urlBuilder
			.setEndpoint(`${INVOICES}/${invoiceId}/download-invoice-details`)
			.setQueryParams(
				new URLSearchParams({
					...(endDate ? { endDate } : {}),
					...(startDate ? { startDate } : {}),
					...(employeeId ? { employeeId } : {}),
					...(discrepancyCategory ? { discrepancyCategory } : {}),
					...(invoiceTransactionType ? { invoiceTransactionType } : {}),
				}),
			)
			.url()

		return axios.get(url, { responseType: 'blob', timeout: 100000 })
	}

	static recalculateInvoice(invoiceId: string): Promise<void> {
		const url = urlBuilder.setEndpoint(`${INVOICES}/${invoiceId}/recalculate`).url()

		return axios.put(url, null, {
			timeout: 15000,
		})
	}

	// There is a possibility that voiding works and recreating fails, but this is an accepted risk. -ZNW
	// See https://dev.azure.com/CorestreamDevOps/Corestream/_workitems/edit/27212
	static async regenerateInvoice({ invoiceId }: RegenerateInvoiceByIdRequest): Promise<void> {
		const voidUrl = urlBuilder.setEndpoint(`${INVOICES}/${invoiceId}/void`).url()
		const recreateUrl = urlBuilder.setEndpoint(`${INVOICES}/${invoiceId}/recreate`).url()

		const voidResponse = await axios.post(voidUrl, null)
		if (voidResponse.status > 299) {
			return Promise.reject(new Error('Failed to void the invoice. Unable to regenerate.'))
		}

		await axios.post(recreateUrl, null)
	}
}
